import React, { useEffect, useState } from "react";
import { getAuth, checkActionCode, applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { useNavigate } from "react-router-dom";

export default function ActionPage() {
  const [status, setStatus] = useState("loading"); // loading, success, error, reset
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [oobCode, setOobCode] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const initAction = async () => {
      const params = new URLSearchParams(window.location.search);
      const mode = params.get("mode");
      const code = params.get("oobCode");

      if (!mode || !code) {
        setStatus("error");
        setMessage("❌ Invalid action link.");
        return;
      }

      const auth = getAuth();

      try {
        // ---------------- EMAIL VERIFICATION ----------------
        if (mode === "verifyEmail") {
          setMessage("⏳ Verifying your email...");

          let email;
          try {
            const info = await checkActionCode(auth, code); // get email
            email = info.data.email;

            if (!email) {
              setStatus("error");
              setMessage("❌ Could not retrieve email from verification link.");
              return;
            }
          } catch (err) {
            setStatus("error");
            setMessage("❌ Verification link is invalid or expired.");
            return;
          }

          setVerifiedEmail(email);

          try {
            const response = await fetch("https://razorpayapi-hhkhmg7zla-uc.a.run.app/verify-email", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email, oobCode: code }), // ✅ send both fields
            });

            const data = await response.json();
            if (!response.ok) {
              console.error("Cloud Function error:", data.error);
              setStatus("error");
              setMessage(`❌ ${data.error || "Verification failed"}`);
              return;
            }

            setStatus("success");
            setMessage("✅ Email verified! Redirecting to Sign In...");
            setTimeout(() => navigate("/signin"), 2000);
          } catch (err) {
            console.error("Cloud Function request failed:", err);
            setStatus("error");
            setMessage("❌ Verification request failed. Try again.");
          }
        }

        // ---------------- PASSWORD RESET ----------------
        if (mode === "resetPassword") {
          try {
            await verifyPasswordResetCode(auth, code);
            setOobCode(code);
            setStatus("reset");
          } catch (err) {
            console.error("verifyPasswordResetCode Error:", err);
            setStatus("error");
            setMessage("❌ Reset link is invalid or expired.");
          }
        }
      } catch (err) {
        console.error("Action Error:", err);
        setStatus("error");
        setMessage("❌ Something went wrong. Please try again.");
      }
    };

    initAction();
  }, [navigate]);

  // ---------------- RESET PASSWORD HANDLER ----------------
  const handlePasswordReset = async () => {
    if (!newPassword) {
      setMessage("❌ Please enter a new password.");
      return;
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(newPassword)) {
      setMessage("❌ Password must include at least 1 uppercase, 1 lowercase, 1 number, and 8+ characters.");
      return;
    }

    try {
      setLoading(true);
      const auth = getAuth();
      await confirmPasswordReset(auth, oobCode, newPassword);

      setStatus("success");
      setMessage("✅ Password reset successfully! Redirecting to Sign In...");
      setTimeout(() => navigate("/signin"), 2000);
    } catch (err) {
      console.error("confirmPasswordReset Error:", err);
      setStatus("error");
      switch (err.code) {
        case "auth/expired-action-code":
          setMessage("❌ Reset link has expired.");
          break;
        case "auth/invalid-action-code":
          setMessage("❌ Reset link is invalid.");
          break;
        case "auth/user-not-found":
          setMessage("❌ User not found.");
          break;
        default:
          setMessage("❌ Couldn't reset password. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: "70px", textAlign: "center" }}>
      {status === "loading" && <p>Processing...</p>}
      {loading && <p>⏳ Please wait...</p>}

      {status === "error" && <h3 style={{ color: "red" }}>{message}</h3>}

      {status === "success" && verifiedEmail && (
        <div>
          <h3 style={{ color: "green" }}>{message}</h3>
          <p><strong>Email:</strong> {verifiedEmail}</p>
        </div>
      )}

      {status === "reset" && (
        <div>
          <h3>Enter New Password</h3>
          <input
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <br /><br />
          <button onClick={handlePasswordReset} disabled={loading}>
            {loading ? "Resetting..." : "Reset Password"}
          </button>
          <p style={{ color: "gray" }}>
            Minimum 8 characters, must include uppercase, lowercase and number.
          </p>
        </div>
      )}
    </div>
  );
}
